import { useState, useEffect } from 'react';
import { 
  FAQ, 
  FAQConfig, 
  subscribeToFAQConfig, 
  trackFAQView, 
  markFAQHelpful,
  groupFAQsByCategory 
} from '@/services/faqService';

interface UseFAQReturn {
  faqs: FAQ[];
  groupedFAQs: Record<string, FAQ[]>;
  categories: string[];
  loading: boolean;
  error: string | null;
  trackView: (faqId: string) => Promise<void>;
  trackViewOnOpen: (faqId: string, isOpen: boolean) => Promise<void>;
  markHelpful: (faqId: string, isHelpful: boolean) => Promise<void>;
  votedFAQs: Set<string>;
}

export const useFAQ = (): UseFAQReturn => {
  const [faqConfig, setFAQConfig] = useState<FAQConfig>({ faqs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votedFAQs, setVotedFAQs] = useState<Set<string>>(new Set());
  const [viewedFAQs, setViewedFAQs] = useState<Set<string>>(new Set());

  // Load voted FAQs from localStorage
  useEffect(() => {
    const storedVotes = localStorage.getItem('faq-votes');
    if (storedVotes) {
      try {
        const votes = JSON.parse(storedVotes);
        setVotedFAQs(new Set(votes));
      } catch (error) {
        // Silent error handling
      }
    }
  }, []);

  // Subscribe to real-time FAQ updates
  useEffect(() => {
    const unsubscribe = subscribeToFAQConfig((config) => {
      setFAQConfig(config);
      setLoading(false);
      setError(null);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Track FAQ view
  const trackView = async (faqId: string) => {
    try {
      await trackFAQView(faqId);
    } catch (error) {
      // Silent error handling
    }
  };

  // Track FAQ view only when opened (not closed)
  const trackViewOnOpen = async (faqId: string, isOpen: boolean) => {
    try {
      if (isOpen && !viewedFAQs.has(faqId)) {
        await trackFAQView(faqId);
        setViewedFAQs(prev => new Set(prev).add(faqId));
      }
    } catch (error) {
      // Silent error handling
    }
  };

  // Mark FAQ as helpful/not helpful
  const markHelpful = async (faqId: string, isHelpful: boolean) => {
    try {
      // Check if user has already voted on this FAQ
      if (votedFAQs.has(faqId)) {
        return;
      }

      await markFAQHelpful(faqId, isHelpful);
      
      // Update local storage and state to prevent multiple votes
      const newVotedFAQs = new Set(votedFAQs);
      newVotedFAQs.add(faqId);
      setVotedFAQs(newVotedFAQs);
      
      localStorage.setItem('faq-votes', JSON.stringify(Array.from(newVotedFAQs)));
    } catch (error) {
      setError('Failed to submit feedback. Please try again.');
    }
  };

  // Group FAQs by category
  const groupedFAQs = groupFAQsByCategory(faqConfig.faqs);
  
  // Get unique categories
  const categories = Object.keys(groupedFAQs).sort();

  return {
    faqs: faqConfig.faqs,
    groupedFAQs,
    categories,
    loading,
    error,
    trackView,
    trackViewOnOpen,
    markHelpful,
    votedFAQs
  };
}; 